import React, { useState } from 'react';

import { schemas } from '@recurrency/core-api-schema';
import { TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';

import { PositiveNumberInput } from 'pages/purchasing/PlanningPage/modals/PositiveNumberInput';

import { Alert } from 'components/Alert';
import { Button } from 'components/Button';
import { AsyncButton } from 'components/Button/AsyncButton';
import { FlexSpace } from 'components/FlexSpace';
import { Modal } from 'components/Modal';
import { PropertyListItem } from 'components/PropertyListItem';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch } from 'utils/api';
import { createSubmissionNotification } from 'utils/submissionNotification';

export function MaxShelfLifeEditModal({
  itemUid,
  itemCode,
  initialMaxShelfLifeDays,
  showAllLocationsWarning,
  onClose,
  onSaved,
}: {
  itemUid: string;
  itemCode: string;
  initialMaxShelfLifeDays?: number | null;
  /** Set when opened from a location-scoped view (Planning, Purchasing), where it's easy to assume the change is local. */
  showAllLocationsWarning?: boolean;
  onClose: (saved?: boolean) => void;
  onSaved?: () => void;
}) {
  const { activeTenant } = useGlobalApp();
  const [maxShelfLifeDays, setMaxShelfLifeDays] = useState<number | undefined>(
    () => initialMaxShelfLifeDays ?? undefined,
  );

  const isUnchanged = maxShelfLifeDays === (initialMaxShelfLifeDays ?? undefined);
  const isInvalid = maxShelfLifeDays !== undefined && (!Number.isInteger(maxShelfLifeDays) || maxShelfLifeDays < 1);

  const handleSubmit = async () => {
    const submitNotification = createSubmissionNotification({
      entityName: 'Max shelf life',
      submittingMessage: 'Updating max shelf life',
      expectedWaitSeconds: 15,
      erpType: activeTenant.erpType,
    });

    try {
      await coreApiFetch(schemas.items.putItemSettings, {
        pathParams: { itemUid },
        bodyParams: {
          maxShelfLifeDays: maxShelfLifeDays ?? null,
        },
      });
      onSaved?.();
      onClose(true);
      submitNotification.success({
        successMessage: `Successfully updated max shelf life for item #${itemCode}`,
      });
    } catch (err) {
      submitNotification.error(err);
    }
  };

  return (
    <Modal
      visible
      title={`Update Max Shelf Life for Item #${itemCode}`}
      onCancel={() => onClose(false)}
      destroyOnClose
      footer={
        <>
          <Button onClick={() => onClose(false)}>Cancel</Button>
          <AsyncButton
            onClick={handleSubmit}
            type="primary"
            permissionsRequired={[TenantUserPermission.ActionEditItemSupplierDetails]}
            disabled={isUnchanged || isInvalid}
          >
            Update
          </AsyncButton>
        </>
      }
      width={480}
    >
      <FlexSpace direction="column" gap={16}>
        {showAllLocationsWarning && (
          <Alert
            type="warning"
            message={`This change applies to item #${itemCode} at all locations, not just the location you are viewing.`}
          />
        )}
        <span>Limits how many days of supply Recurrency will recommend purchasing. Clear to remove the limit.</span>
        <PropertyListItem
          label="Max Shelf Life (days)"
          value={
            <PositiveNumberInput
              allowClear
              requiresInteger
              min={1}
              onChange={setMaxShelfLifeDays}
              value={maxShelfLifeDays}
            />
          }
        />
      </FlexSpace>
    </Modal>
  );
}
